import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "react-color-palette/css";
import "../styles/CustomColorPickerInput.css";
import { Close as CloseIcon } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { Input } from "@air/primitive-input";
import { hexToRgb, rgbToHsv } from "@air/utils-color";
import { reportErrorToBugsnag } from "@air/utils-error";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ColorPicker as ReactColorPalettePicker, useColor } from "react-color-palette";
const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}){1,2}$/;
const HIDDEN_INPUT_TYPES = [
    "rgb",
    "hsv",
    "hex"
];
export const CustomColorPickerInput = /*#__PURE__*/ memo((param)=>{
    let { defaultColor = "#FAAEA6", onSave } = param;
    const [color, setColor] = useColor(defaultColor);
    const [hexInput, setHexInput] = useState(color.hex);
    const handleSave = useCallback(()=>{
        onSave(color.hex);
    }, [
        color.hex,
        onSave
    ]);
    const handleClear = useCallback(()=>{
        setHexInput("");
    }, []);
    const handleHexChange = useCallback((e)=>{
        const newHex = e.target.value.replace(/[^0-9a-fA-F#]/g, ""); // Remove invalid characters
        setHexInput(newHex);
        // Only try to update color if we have a complete hex code
        if (newHex.length >= 3) {
            const hexWithHash = newHex.startsWith("#") ? newHex : "#".concat(newHex);
            // Check if it's a valid hex color (3 or 6 digits)
            if (HEX_COLOR_REGEX.test(hexWithHash)) {
                try {
                    const rgb = hexToRgb(hexWithHash);
                    if (rgb) {
                        const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
                        setColor({
                            hex: hexWithHash,
                            rgb: {
                                r: rgb.r,
                                g: rgb.g,
                                b: rgb.b,
                                a: 1
                            },
                            hsv: {
                                h: hsv.h,
                                s: hsv.s,
                                v: hsv.v,
                                a: 1
                            }
                        });
                    }
                } catch (error) {
                    reportErrorToBugsnag({
                        error,
                        context: "Failed to set color from hex input",
                        metadata: {
                            hexInput: newHex,
                            hexWithHash
                        }
                    });
                }
            }
        }
    }, [
        setColor
    ]);
    // Update input when color changes from picker
    useEffect(()=>{
        setHexInput(color.hex);
    }, [
        color
    ]);
    // Check if the current hex input is valid
    const isValidHex = useMemo(()=>{
        if (!hexInput) return false;
        const hexWithHash = hexInput.startsWith("#") ? hexInput : "#".concat(hexInput);
        return HEX_COLOR_REGEX.test(hexWithHash);
    }, [
        hexInput
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "color-picker-container",
        children: [
            /*#__PURE__*/ _jsx(ReactColorPalettePicker, {
                onChange: setColor,
                color: color,
                hideInput: HIDDEN_INPUT_TYPES,
                hideAlpha: true,
                "data-testid": "COLOR_PICKER"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex w-full items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative w-full min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx(Input, {
                                type: "text",
                                value: hexInput,
                                onChange: handleHexChange,
                                className: "w-full",
                                placeholder: "#000000",
                                "data-testid": "CUSTOM_COLOR_HEX_INPUT"
                            }),
                            hexInput && /*#__PURE__*/ _jsx("button", {
                                onClick: handleClear,
                                className: "absolute right-2 top-1/2 -translate-y-1/2 p-1",
                                type: "button",
                                "data-testid": "CUSTOM_COLOR_CLEAR_BUTTON",
                                children: /*#__PURE__*/ _jsx(CloseIcon, {
                                    className: "size-3"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: handleSave,
                        appearance: "outline",
                        color: "grey",
                        disabled: !isValidHex,
                        "data-testid": "CUSTOM_COLOR_SAVE_BUTTON",
                        children: "Save"
                    })
                ]
            })
        ]
    });
});
CustomColorPickerInput.displayName = "CustomColorPickerInput";
